// 用户管理 API 服务
import request from '../utils/request';

// 用户角色枚举
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

// 用户状态枚举
export enum UserStatus {
  DISABLED = 2,
  ENABLED = 1,
}

// 用户信息接口
export interface User {
  id: number;
  phone: string;
  role: UserRole;
  status: UserStatus;
  name?: string;
  company?: string;
  address?: string;
  remark?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  lastLoginIp?: string;
}

// 创建用户请求
export interface CreateUserRequest {
  phone: string;
  password: string;
  role: UserRole;
  name?: string;
  company?: string;
  address?: string;
  remark?: string;
}

// 更新用户请求
export interface UpdateUserRequest {
  name?: string;
  company?: string;
  address?: string;
  remark?: string;
}

// 用户列表查询参数
export interface ListUsersParams {
  phone?: string;
  role?: string;
  status?: number;
  page?: number;
  pageSize?: number;
}

// 用户列表响应
export interface ListUsersResponse {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
}

// ============ 用户管理 API ============

/**
 * 创建用户
 */
export function createUser(data: CreateUserRequest) {
  return request.post('/users', data);
}

/**
 * 获取用户列表
 */
export function listUsers(params?: ListUsersParams) {
  return request.get('/users', { params });
}

/**
 * 获取用户详情
 */
export function getUser(id: number) {
  return request.get(`/users/${id}`);
}

/**
 * 更新用户信息
 */
export function updateUser(id: number, data: UpdateUserRequest) {
  return request.put(`/users/${id}`, data);
}

/**
 * 更新用户状态
 */
export function updateUserStatus(id: number, status: UserStatus) {
  return request.put(`/users/${id}/status`, { status });
}

/**
 * 用户或管理员修改自己的密码（需要旧密码）
 */
export function updateSelfPassword(oldPassword: string, newPassword: string) {
  return request.put(`/password/self`, {
    oldPassword: oldPassword,
    newPassword: newPassword,
  });
}

/**
 * 管理员重置其他用户密码（不需要旧密码）
 */
export function resetUserPassword(id: number, newPassword: string) {
  return request.put(`/users/${id}/password`, {
    newPassword: newPassword,
  });
}

/**
 * 删除用户
 */
export function deleteUser(id: number) {
  return request.delete(`/users/${id}`);
}

// ============ 用户设备授权 API ============

export interface DeviceInfo {
  deviceId: string;
  type?: number;
  serialNumber?: string;
  status: number;
}

export interface UserAuthorizationsResponse {
  userId: number;
  devices: DeviceInfo[];
  total: number;
}

/**
 * 批量授权设备给用户
 */
export function batchAuthorizeDevices(userId: number, deviceIds: string[], remark?: string) {
  return request.post(`/users/${userId}/authorizations`, {
    deviceIds: deviceIds,
    remark,
  });
}

/**
 * 获取用户授权的设备列表
 */
export function getUserAuthorizations(userId: number, status?: number) {
  return request.get(`/users/${userId}/authorizations`, {
    params: status !== undefined ? { status } : undefined,
  });
}

/**
 * 批量撤销用户设备授权
 */
export function batchRevokeAuthorizations(userId: number, deviceIds: string[]) {
  return request.delete(`/users/${userId}/authorizations`, {
    data: { deviceIds: deviceIds },
  });
}

/**
 * 更新授权状态
 */
export function updateAuthorizationStatus(userId: number, deviceIds: string[], status: number) {
  return request.put(`/users/${userId}/authorizations/status`, {
    deviceIds: deviceIds,
    status,
  });
}

/**
 * 查询设备授权给了哪些用户
 */
export function getDeviceAuthorizedUsers(deviceId: string, status?: number) {
  return request.get(`/devices/${deviceId}/users`, {
    params: status !== undefined ? { status } : undefined,
  });
}

/**
 * 搜索设备用于授权
 */
export function searchDevicesForAuthorization(userId: number, keyword?: string, page = 1, pageSize = 50) {
  return request.get(`/users/${userId}/devices/search`, {
    params: {
      userId,
      keyword,
      page,
      pageSize: pageSize,
    },
  });
}
